import React, { forwardRef, ReactElement, ReactNode, useMemo } from 'react';
import { DOMProps, HoverEvents } from '@react-types/shared';
import { useHover } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { TestProps } from '../../internal/types/testTypes';
import { useHStack } from '../../hooks/useStack';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { useInvertedTheme } from '../../lib/theme';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { HStack, Stack } from '../Stack';

import { LabelSize } from './types';

import './Label.css';

const cls = cn('Label');

type DeprecatedColor = 'logo-blue' | 'purple' | 'black' | 'gray-2';
export type LabelColor =
    | 'info'
    | 'error'
    | 'warning'
    | 'message'
    | 'success'
    | 'blue-negative'
    | 'purple-negative'
    | 'red-negative'
    | 'orange-negative'
    | 'black-negative'
    | 'green-negative'
    | 'ai'
    | DeprecatedColor;

export interface LabelProps extends TestProps, HoverEvents, DOMProps {
    /**
     * Текст или единственная иконка внутри элемента.
     * При передаче числа в качестве children расстояние между аддоном и текстом уменьшится (по дизайну).
     * Для передачи иконки в качестве единственного содержимого - использовать в сочетании с `withIconOnly`.
     */
    children?: ReactNode;

    /**
     * Признак использования иконки в качестве единственного содержимого (влияет на выравнивание элемента).
     */
    withIconOnly?: boolean;

    /**
     * Паддинги элемента в случае когда иконка единственное содержимое
     * @default '2xs'
     */
    size?: LabelSize;

    /**
     * Цветовая палитра элемента
     * @default message
     */
    color?: LabelColor;

    /**
     * Признак отключения элемента
     * @default false
     */
    isDisabled?: boolean;

    /**
     * Аддон слева от текста (иконка).
     * Выравнивание аддона задаётся в соответствии с размером текста и определено дизайн-системой.
     */
    addonLeft?: ReactElement<{ className: string }>;

    /**
     * Аддон справа от текста (иконка).
     * Выравнивание аддона задаётся в соответствии с размером текста и определено дизайн-системой.
     */
    addonRight?: ReactElement<{ className: string }>;

    /**
     * Названия классов для элемента
     */
    className?: string;
}

const skinMap: Record<LabelColor, SkinRole | SkinColor | undefined> = {
    // Тут ролевые скины
    info: 'info',
    error: 'error',
    warning: 'warning',
    success: 'success',
    message: 'message-lc',
    // Тут обычные цветовые
    purple: 'purple-high-contrast',
    black: 'gray-neutral',
    'gray-2': 'gray-opaque',
    'green-negative': 'green-neutral',
    'blue-negative': 'blue-neutral',
    'purple-negative': 'purple-neutral',
    'red-negative': 'red-neutral',
    'orange-negative': 'orange-neutral',
    'black-negative': 'gray-neutral',
    ai: 'ai',
    // TODO DIRECT-238219 Для этого цвета нет скина, он удалится
    'logo-blue': undefined,
};

/**
 * Компонент для создания сносок
 */
export const Label = genericMemo(
    forwardRef<HTMLDivElement, LabelProps>(function Label(props, forwardedRef) {
        const {
            children,
            withIconOnly,
            color = 'message',
            size = '2xs',
            isDisabled,
            addonLeft,
            addonRight,
            className,
            onHoverStart,
            onHoverEnd,
            onHoverChange,
            ...otherProps
        } = props;

        const sizeSkinClass = getSizeSkin(size);
        const textSize = 'current-main';

        const { addonClasses } = useInternalVerticalAlignWithSkin({ textSize, addonType: 'icon' });
        const { hoverProps } = useHover({ isDisabled, onHoverStart, onHoverChange, onHoverEnd });

        // основной элемент: текст или иконка в центре
        const content = useMemo(() => {
            return (
                children &&
                (withIconOnly ? (
                    <Stack className={cls('icon', [addonClasses])}>{children}</Stack>
                ) : (
                    <TextGenericWithSkin
                        className={cls('text', [addonClasses])}
                        skinSize={size}
                        textSize={textSize}
                        color={color === 'logo-blue' ? 'yellow-2' : 'current'}
                        bold
                    >
                        {children}
                    </TextGenericWithSkin>
                ))
            );
        }, [children, withIconOnly, addonClasses, size, color]);

        const addonLeftComponent = useMemo(() => {
            return addonLeft && <HStack className={cls('addonLeft', [addonClasses])}>{addonLeft}</HStack>;
        }, [addonClasses, addonLeft]);

        const addonRightComponent = useMemo(() => {
            return addonRight && <HStack className={cls('addonRight', [addonClasses])}>{addonRight}</HStack>;
        }, [addonClasses, addonRight]);

        const stackClasses = useHStack({ gap: 'current-geometry' });
        const skinColor = skinMap[color];
        const skinClass = skinColor ? getColorSkin(skinColor, { isDisabled }) : undefined;
        const invertedTheme = useInvertedTheme();

        return (
            <div
                className={cls(
                    {
                        disabled: isDisabled,
                        withIconOnly: withIconOnly,
                        color,
                        size,
                    },
                    [sizeSkinClass, stackClasses, skinClass, color === 'black' ? invertedTheme : '', className],
                )}
                ref={forwardedRef}
                data-testid={props['data-testid']}
                {...hoverProps}
                {...otherProps}
            >
                {addonLeftComponent}
                {content}
                {addonRightComponent}
            </div>
        );
    }),
);
